/**
 * auoOverrides.ts — owner CORRECTIONS over the canonical AUO×zone XLS table
 * (Slice 5, 2026-06-08 — the inline disposition-correction loop).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS
 * ─────────────────────────────────────────────────────────────────────────
 *  The canonical dispositions (functions/.../auo_dispositions_canonical.json)
 *  were DERIVED from the owner's terse XLS effect text and are sometimes wrong
 *  (owner's example: Toilet×SSE "low confidence, physical strength" → derived
 *  `neutral`, but BOTH qualities being LOW is a NEGATIVE verdict) — or simply
 *  absent ("No ruling yet" / db-miss). The owner must CORRECT a (AUO × zone)
 *  cell and have it persist for ALL future use. This module is the durable
 *  learning store.
 *
 *  PERSISTENCE = the Firestore collection `vastu_auo_overrides`, doc id
 *    `{canonicalAuoKey}__{ZONE}` (slashes sanitised to '∕' — path-safe). One doc
 *    per corrected cell. The Stage-5 callable (`suggestAuoFixtures`) reads this
 *    collection (admin SDK) and MERGES it over the canonical table — override
 *    WINS — so a corrected cell flows into every future review (and, later, the
 *    report compose path, which will reuse the SAME store).
 *
 *  TODO(override-reconciliation): a future batch folds these overrides back into
 *  `auo_dispositions_canonical.json` + the owner's `Vastu File 2.xlsx` so the
 *  master stays authoritative; folded overrides are then retired. NOT built here.
 *
 *  Pure shape builders above the firebase import; the impure read/write below.
 * ─────────────────────────────────────────────────────────────────────────
 */

import type { AuoSuggestion } from './auoMarks';

/** The disposition labels the owner can choose, mapped to the severity tag. The
 *  callable's `tagToSeverity` maps OK→1, TOL→2, NO→3 (matching the compose path);
 *  the UI shows the LABEL, the tag drives the severity colour ramp + report. */
export const OVERRIDE_DISPOSITIONS = [
  'favourable',
  'neutral',
  'caution',
  'critical',
] as const;
export type OverrideDisposition = (typeof OVERRIDE_DISPOSITIONS)[number];

/** disposition label → matrix tag (OK/TOL/NO). Mirrors the canonical table's own
 *  label↔tag pairing so a corrected cell carries a consistent tag for severity. */
export function tagForDisposition(disp: OverrideDisposition): 'OK' | 'TOL' | 'NO' {
  switch (disp) {
    case 'favourable':
      return 'OK';
    case 'critical':
      return 'NO';
    case 'caution':
      return 'NO'; // caution is still a NO-class avoid (severity 3) — owner remediates
    case 'neutral':
    default:
      return 'TOL';
  }
}

/** The persisted override doc shape (mirrors the callable's `overrideToCell`). */
export interface AuoOverrideRecord {
  /** canonical AUO key (a real key in the dispositions table), e.g. 'Toilet'. */
  auo_key: string;
  /** the canonical name (== auo_key today; kept distinct for future display). */
  canonical_name: string;
  /** the 16-zone code, upper-cased. */
  zone: string;
  /** matrix tag derived from the disposition. */
  tag: 'OK' | 'TOL' | 'NO';
  /** the corrected disposition label. */
  disposition: OverrideDisposition;
  /** the owner's VERBATIM effect text (the ground truth). */
  effect: string;
  /** the remedy / management text, or null. */
  management: string | null;
  /** optional corrective colour/metal (carried through if the owner sets them). */
  corrective_colour?: string | null;
  corrective_metal?: string | null;
  /** who corrected it (staff/owner uid or label). */
  edited_by: string | null;
  /** what the cell was BEFORE the correction ('vastu_xls' | 'db-miss' | 'override'). */
  prior_source: string | null;
  /** NEW (additive, self-describing — Silas's scope design §1.3). Which layer the
   *  doc lives in: 'project' (per-project subcollection) | 'global'. The PATH is
   *  the SSOT for scope; this is a denormalized convenience for audit/export.
   *  Global docs omit it (absent ⇒ treat as 'global'). */
  override_scope?: 'project' | 'global';
  /** NEW — denormalized projectId (project-scoped docs only; the path is SSOT). */
  project_id?: string;
}

/** PURE — the doc-id slug for a (canonical AUO key × zone) override. MUST match
 *  the callable's `overrideKey` exactly so reads + writes address the same doc. */
export function overrideKey(auoKey: string, zone: string): string {
  const safe = String(auoKey || '').replace(/\//g, '∕');
  return `${safe}__${String(zone || '').trim().toUpperCase()}`;
}

/** PURE — assemble an override record from the edit-form fields. */
export function buildOverrideRecord(input: {
  auoKey: string;
  zone: string;
  disposition: OverrideDisposition;
  effect: string;
  management?: string | null;
  correctiveColour?: string | null;
  correctiveMetal?: string | null;
  editedBy?: string | null;
  priorSource?: string | null;
}): AuoOverrideRecord {
  const disp = input.disposition;
  return {
    auo_key: input.auoKey,
    canonical_name: input.auoKey,
    zone: String(input.zone || '').trim().toUpperCase(),
    tag: tagForDisposition(disp),
    disposition: disp,
    effect: (input.effect || '').trim(),
    management: (input.management || '').trim() || null,
    corrective_colour: input.correctiveColour || null,
    corrective_metal: input.correctiveMetal || null,
    edited_by: input.editedBy || null,
    prior_source: input.priorSource || null,
  };
}

/** PURE — project an override record onto the `AuoSuggestion` shape the card
 *  renders, so the UI can reflect a freshly-saved correction IMMEDIATELY without
 *  waiting for a callable round-trip. Severity follows the tag (NO→3, TOL→2, OK→1). */
export function overrideToSuggestion(rec: AuoOverrideRecord): AuoSuggestion {
  const sevByTag: Record<string, { severity: number; severity_label: string }> = {
    NO: { severity: 3, severity_label: 'critical' },
    TOL: { severity: 2, severity_label: 'remedy' },
    OK: { severity: 1, severity_label: 'ok' },
  };
  const sev = sevByTag[rec.tag] || { severity: 0, severity_label: 'unspecified' };
  return {
    zone: rec.zone,
    normalised: rec.canonical_name,
    tag: rec.tag,
    disposition: rec.disposition,
    effect: rec.effect || null,
    management: rec.management || null,
    corrective_colour: rec.corrective_colour ?? null,
    corrective_metal: rec.corrective_metal ?? null,
    source: 'override',
    severity: sev.severity,
    severity_label: sev.severity_label,
    reason: `${rec.canonical_name} in ${rec.zone}: owner-corrected — ${rec.disposition}.`,
    recognised: true,
    // carry the scope through so the card can label "this project" vs "everywhere"
    // immediately (a global rec without the field reads as 'global').
    override_scope: rec.override_scope ?? 'global',
  };
}

/* ─────────────────────────── impure (Firestore read/write) ─────────────────────────── */

/**
 * Persist (create or overwrite) one (AUO × zone) override to
 * `vastu_auo_overrides/{auoKey__ZONE}` with a server `edited_at` timestamp.
 * Admin/owner-gated by the Firestore rule — a non-admin write rejects (the caller
 * surfaces the error). Returns the doc id on success. Throws on failure so the UI
 * can tell the owner the correction did NOT save (unlike telemetry, this is
 * authoritative learning data — a silent drop would be wrong).
 */
export async function saveAuoOverride(rec: AuoOverrideRecord): Promise<string> {
  const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  const id = overrideKey(rec.auo_key, rec.zone);
  await setDoc(
    doc(db, 'vastu_auo_overrides', id),
    { ...rec, edited_at: serverTimestamp() },
    { merge: true },
  );
  return id;
}

/**
 * Persist (create or overwrite) one (AUO × zone) override to the PROJECT-scoped
 * subcollection `projects/{projectId}/auo_overrides/{auoKey__ZONE}` (Silas's
 * scope design §3.2). Same record shape + same doc-id slug as the global writer,
 * plus the two self-describing fields (`override_scope:'project'`, `project_id`)
 * and a server `edited_at`. The PATH is the SSOT for scope. ADMIN-ONLY by the
 * (Vex-authored) Firestore rule — a non-admin write rejects and the caller
 * surfaces the error. Does NOT touch the global collection. Throws on failure so
 * the UI can tell the admin the correction did NOT save. Idempotent / re-runnable.
 */
export async function saveProjectAuoOverride(
  rec: AuoOverrideRecord,
  projectId: string,
): Promise<string> {
  const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  const id = overrideKey(rec.auo_key, rec.zone);
  await setDoc(
    doc(db, 'projects', projectId, 'auo_overrides', id),
    { ...rec, override_scope: 'project', project_id: projectId, edited_at: serverTimestamp() },
    { merge: true },
  );
  return id;
}
